﻿import React, { useState, useEffect, useMemo } from 'react'
import { Text, Heading, Input, Select, SortableTableHeader, LoadingSpinner, Button } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { supabase } from '../lib/supabase'
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  XMarkIcon,
  ServerIcon,
  ChatBubbleLeftRightIcon,
  XCircleIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline'

interface MessageData {
  id: number
  message: string
  type: string
  to: string
  origin: string | null
  return: string | null
  created_at: string
  sent_date: string | null
}

const isMessageFailed = (item: MessageData) => item.return != null && String(item.return).trim() !== ''

const parseSentDateMs = (value: string | null | undefined): number | null => {
  if (!value?.trim()) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`).getTime()
  }
  const ms = new Date(value).getTime()
  return Number.isNaN(ms) ? null : ms
}

const MensagensAtivas = () => {
  const [messagesData, setMessagesData] = useState<MessageData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroDestinatario, setFiltroDestinatario] = useState('')
  const [filtroMensagem, setFiltroMensagem] = useState('')
  const [filtroOrigem, setFiltroOrigem] = useState('')
  const [filtroStatus, setFiltroStatus] = useState<'all' | 'recebido' | 'falhou'>('all')
  const [filtroDataInicio, setFiltroDataInicio] = useState('')
  const [filtroDataFim, setFiltroDataFim] = useState('')
  const [tiposDisponiveis, setTiposDisponiveis] = useState<string[]>([])
  const [origensDisponiveis, setOrigensDisponiveis] = useState<string[]>([])
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState<MessageData | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  
  // Estados de ordenação
  const [sortBy, setSortBy] = useState<'type' | 'to' | 'message' | 'origin' | 'return' | 'sent_date'>('sent_date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true)
        
        // Buscar todos os registros usando paginação
        const allData: MessageData[] = []
        const batchSize = 1000
        let from = 0
        let hasMore = true

        while (hasMore) {
          const { data, error } = await supabase
            .from('messages_sent')
            .select('*')
            .order('sent_date', { ascending: false })
            .range(from, from + batchSize - 1)

          if (error) {
            throw error
          }

          if (data && data.length > 0) {
            allData.push(...(data as MessageData[]))
            
            // Se retornou menos que o batchSize, não há mais dados
            if (data.length < batchSize) {
              hasMore = false
            } else {
              from += batchSize
            }
          } else {
            hasMore = false
          }
        }

        setMessagesData(allData)
        
        // Extrair tipos e origens únicos dos dados
        const tipos = allData.map(item => item.type).filter(tipo => tipo)
        const tiposUnicos = Array.from(new Set(tipos)).sort()
        setTiposDisponiveis(tiposUnicos)

        const origens = allData
          .map(item => item.origin)
          .filter((origem): origem is string => !!origem?.trim())
        const origensUnicas = Array.from(new Set(origens)).sort()
        setOrigensDisponiveis(origensUnicas)
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchMessages()
  }, [])

  const messagesFiltrados = useMemo(() => {
    const dataInicioMs = filtroDataInicio
      ? new Date(`${filtroDataInicio}T00:00:00`).getTime()
      : null
    const dataFimMs = filtroDataFim
      ? new Date(`${filtroDataFim}T23:59:59.999`).getTime()
      : null

    const filtered = messagesData.filter(item => {
      const matchTipo = !filtroTipo || item.type === filtroTipo
      const matchDestinatario = !filtroDestinatario || item.to.toLowerCase().includes(filtroDestinatario.toLowerCase())
      const matchMensagem = !filtroMensagem || item.message.toLowerCase().includes(filtroMensagem.toLowerCase())
      const matchOrigem = !filtroOrigem || (item.origin ?? '') === filtroOrigem
      const failed = isMessageFailed(item)
      const matchStatus =
        filtroStatus === 'all' ||
        (filtroStatus === 'falhou' && failed) ||
        (filtroStatus === 'recebido' && !failed)

      const sentAtMs = parseSentDateMs(item.sent_date)
      const matchDataInicio = dataInicioMs == null || (sentAtMs != null && sentAtMs >= dataInicioMs)
      const matchDataFim = dataFimMs == null || (sentAtMs != null && sentAtMs <= dataFimMs)
      
      return matchTipo && matchDestinatario && matchMensagem && matchOrigem && matchStatus && matchDataInicio && matchDataFim
    })

    // Aplicar ordenação
    return filtered.sort((a, b) => {
      let aValue: any = a[sortBy]
      let bValue: any = b[sortBy]

      // Tratamento especial para campos específicos
      if (sortBy === 'sent_date') {
        aValue = parseSentDateMs(a.sent_date) ?? 0
        bValue = parseSentDateMs(b.sent_date) ?? 0
      } else if (sortBy === 'return') {
        aValue = isMessageFailed(a) ? 1 : 0
        bValue = isMessageFailed(b) ? 1 : 0
      } else {
        aValue = String(aValue ?? '').toLowerCase()
        bValue = String(bValue ?? '').toLowerCase()
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [messagesData, filtroTipo, filtroDestinatario, filtroMensagem, filtroOrigem, filtroStatus, filtroDataInicio, filtroDataFim, sortBy, sortOrder])

  const messageCounts = useMemo(() => {
    let falharam = 0
    let chegaram = 0
    for (const item of messagesFiltrados) {
      if (isMessageFailed(item)) falharam += 1
      else chegaram += 1
    }
    return {
      total: messagesFiltrados.length,
      falharam,
      chegaram,
    }
  }, [messagesFiltrados])

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [filtroTipo, filtroDestinatario, filtroMensagem, filtroOrigem, filtroStatus, filtroDataInicio, filtroDataFim])

  // Calcular dados da paginação
  const totalPages = Math.ceil(messagesFiltrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const messagesPaginados = messagesFiltrados.slice(startIndex, endIndex)

  const handleRowClick = (item: MessageData) => {
    setSelectedMessage(item)
    setIsDetailsModalOpen(true)
  }

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false)
    setSelectedMessage(null)
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as any)
      setSortOrder('asc')
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const getTypeIcon = (type: string) => {
    return <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-500" />
  }

  const getTypeText = (type: string) => {
    return type || 'Não definido'
  }

  const getTypeColor = (type: string) => {
    return 'text-primary-600 dark:text-primary-400'
  }

  const getDestinatarioIcon = (to: string) => {
    return <EnvelopeIcon className="h-5 w-5 text-green-500" />
  }

  const getDestinatarioText = (to: string) => {
    if (!to) return 'Não definido'
    
    // Remove espaços e caracteres especiais para verificar se é telefone
    const cleanTo = to.replace(/\s/g, '')
    
    // Verifica se é um número de telefone (contém apenas dígitos, +, -, (, ))
    const isPhoneNumber = /^[\d\+\-\(\)]+$/.test(cleanTo) && /\d/.test(cleanTo)
    
    if (isPhoneNumber) {
      return formatPhoneNumber(to)
    }
    
    return to
  }

  const getDestinatarioColor = (to: string) => {
    return 'text-green-600 dark:text-green-400'
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString?.trim()) return '—'
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString)
    const date = dateOnly ? new Date(`${dateString}T00:00:00`) : new Date(dateString)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...(dateOnly ? {} : { hour: '2-digit', minute: '2-digit' })
    })
  }

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return 'Sem mensagem'
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return 'Não definido'
    
    // Remove todos os caracteres não numéricos
    const numbers = phone.replace(/\D/g, '')
    
    // Se tem 11 dígitos (celular com DDD)
    if (numbers.length === 11) {
      return `+55 (${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7)}`
    }
    
    // Se tem 10 dígitos (telefone fixo com DDD)
    if (numbers.length === 10) {
      return `+55 (${numbers.slice(0, 2)})${numbers.slice(2, 6)}-${numbers.slice(6)}`
    }
    
    // Se tem 9 dígitos (celular sem DDD)
    if (numbers.length === 9) {
      return `+55 (31)${numbers.slice(0, 5)}-${numbers.slice(5)}`
    }
    
    // Se tem 8 dígitos (telefone fixo sem DDD)
    if (numbers.length === 8) {
      return `+55 (31)${numbers.slice(0, 4)}-${numbers.slice(4)}`
    }
    
    // Se tem 13 dígitos (com código do país 55)
    if (numbers.length === 13 && numbers.startsWith('55')) {
      const ddd = numbers.slice(2, 4)
      const number = numbers.slice(4)
      if (number.length === 9) {
        return `+55 (${ddd})${number.slice(0, 5)}-${number.slice(5)}`
      } else if (number.length === 8) {
        return `+55 (${ddd})${number.slice(0, 4)}-${number.slice(4)}`
      }
    }
    
    // Para outros formatos, retorna o número original
    return phone
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <Text size="lg" className="text-gray-600 dark:text-gray-400">
              Carregando mensagens...
            </Text>
          </div>
        </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <Text size="lg" className="text-red-600 dark:text-red-400">
              Erro ao carregar mensagens: {error}
            </Text>
          </div>
        </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="atendimentos_mensagens_ativas" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
            <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                Mensagens Ativas
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                Histórico de mensagens enviadas pelo sistema via WhatsApp
              </Text>
            </div>

            {/* Topo Colorido - Valores */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {messageCounts.total}
                    </Text>
                    <Text size="sm" className="text-white">
                      Total de Mensagens
                    </Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {messageCounts.falharam}
                    </Text>
                    <Text size="sm" className="text-white">
                      Mensagens que Falharam
                    </Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {messageCounts.chegaram}
                    </Text>
                    <Text size="sm" className="text-white">
                      Mensagens Recebidas
                    </Text>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtros (padrão SilvIA) */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Filtros
                  </Heading>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Tipo
                      </Text>
                      <Select
                        value={filtroTipo}
                        onChange={(e) => setFiltroTipo(e.target.value)}
                        className="w-full"
                      >
                        <option value="">Todos os tipos</option>
                        {tiposDisponiveis.map((tipo) => (
                          <option key={tipo} value={tipo}>
                            {tipo}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Destinatário
                      </Text>
                      <Input
                        type="text"
                        placeholder="Digite o destinatário..."
                        value={filtroDestinatario}
                        onChange={(e) => setFiltroDestinatario(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Mensagem
                      </Text>
                      <Input
                        type="text"
                        placeholder="Digite parte da mensagem..."
                        value={filtroMensagem}
                        onChange={(e) => setFiltroMensagem(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Origem
                      </Text>
                      <Select
                        value={filtroOrigem}
                        onChange={(e) => setFiltroOrigem(e.target.value)}
                        className="w-full"
                      >
                        <option value="">Todas as origens</option>
                        {origensDisponiveis.map((origem) => (
                          <option key={origem} value={origem}>
                            {origem}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Status
                      </Text>
                      <Select
                        value={filtroStatus}
                        onChange={(e) => setFiltroStatus(e.target.value as 'all' | 'recebido' | 'falhou')}
                        className="w-full"
                      >
                        <option value="all">Todos</option>
                        <option value="recebido">Recebido</option>
                        <option value="falhou">Falhou</option>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Data inicial
                      </Text>
                      <Input
                        type="date"
                        value={filtroDataInicio}
                        onChange={(e) => setFiltroDataInicio(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Data final
                      </Text>
                      <Input
                        type="date"
                        value={filtroDataFim}
                        onChange={(e) => setFiltroDataFim(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="md:col-span-2 flex items-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFiltroTipo('')
                          setFiltroDestinatario('')
                          setFiltroMensagem('')
                          setFiltroOrigem('')
                          setFiltroStatus('all')
                          setFiltroDataInicio('')
                          setFiltroDataFim('')
                        }}
                      >
                        Limpar Filtros
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="flex-1 px-6 py-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Histórico de Mensagens
                  </Heading>
                </div>
                
                <div className="flex-1 overflow-auto">
                  <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                      <tr>
                        <SortableTableHeader
                          sortKey="type"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-1/5"
                        >
                          Tipo
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="to"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-1/5"
                        >
                          Destinatário
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="message"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-2/5"
                        >
                          Mensagem
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="origin"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-1/6"
                        >
                          Origem
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="return"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-1/6 text-center"
                        >
                          Status
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="sent_date"
                          currentSort={sortBy}
                          sortOrder={sortOrder}
                          onSort={toggleSort}
                          className="w-1/6 text-center"
                        >
                          Data
                        </SortableTableHeader>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {messagesPaginados.map((item) => (
                        <tr 
                          key={item.id} 
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          onClick={() => handleRowClick(item)}
                        >
                          <td className="px-4 py-4 text-left">
                            <Text size="sm" className={`font-medium ${getTypeColor(item.type)}`}>
                              {getTypeText(item.type)}
                            </Text>
                          </td>
                          <td className="px-4 py-4 text-left">
                            <div className="flex items-center gap-2">
                              {getDestinatarioIcon(item.to)}
                              <Text size="sm" className={`font-medium ${getDestinatarioColor(item.to)}`}>
                                {getDestinatarioText(item.to)}
                              </Text>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-left">
                            <Text size="sm" className="text-gray-700 dark:text-gray-300">
                              {truncateText(item.message)}
                            </Text>
                          </td>
                          <td className="px-4 py-4 text-left">
                            <Text size="sm" className="text-gray-700 dark:text-gray-300">
                              {item.origin?.trim() || '—'}
                            </Text>
                          </td>
                          <td className="px-4 py-4 text-center">
                            {isMessageFailed(item) ? (
                              <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                Falhou
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                Recebido
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <Text size="sm" className="text-gray-500 dark:text-gray-400">
                              {formatDate(item.sent_date)}
                            </Text>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Controles de Paginação */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Text size="sm">
                          Mostrando {startIndex + 1} a {Math.min(endIndex, messagesFiltrados.length)} de {messagesFiltrados.length} registros
                        </Text>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Anterior
                        </button>
                        
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNumber: number;
                            if (totalPages <= 5) {
                              pageNumber = i + 1;
                            } else if (currentPage <= 3) {
                              pageNumber = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNumber = totalPages - 4 + i;
                            } else {
                              pageNumber = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => handlePageChange(pageNumber)}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  currentPage === pageNumber
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Próximo
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
          </PermissionGuard>

      {/* Modal de Detalhes */}
      {isDetailsModalOpen && selectedMessage && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDetailsModal}></div>
            
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Detalhes da Mensagem Ativa
                  </Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    ID: {selectedMessage.id} - Tipo: {selectedMessage.type}
                  </Text>
                </div>
                <button
                  onClick={handleCloseDetailsModal}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Tabela de Configuração */}
              <div className="flex-1 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Campo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          ID
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedMessage.id}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Tipo
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(selectedMessage.type)}
                          <Text size="sm" className={`font-medium ${getTypeColor(selectedMessage.type)}`}>
                            {getTypeText(selectedMessage.type)}
                          </Text>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Destinatário
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="flex items-center gap-2">
                          {getDestinatarioIcon(selectedMessage.to)}
                          <Text size="sm" className={`font-medium ${getDestinatarioColor(selectedMessage.to)}`}>
                            {getDestinatarioText(selectedMessage.to)}
                          </Text>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Mensagem
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="max-w-4xl">
                          <div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                  <Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium ml-2">
                                    Conteúdo da Mensagem
                                  </Text>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-500">
                                  TEXTO
                                </div>
                              </div>
                            </div>
                            <div className="p-6">
                              <div className="text-sm text-gray-900 dark:text-gray-100 font-mono overflow-auto max-h-96 whitespace-pre-wrap break-words leading-loose bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border">
                                {selectedMessage.message || 'Sem mensagem'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Origem
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedMessage.origin?.trim() || '—'}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Status
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        {isMessageFailed(selectedMessage) ? (
                          <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                            Falhou
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            Recebido
                          </span>
                        )}
                      </td>
                    </tr>
                    {isMessageFailed(selectedMessage) && (
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                            Retorno
                          </Text>
                        </td>
                        <td className="px-6 py-4 text-left">
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                            {selectedMessage.return}
                          </Text>
                        </td>
                      </tr>
                    )}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Data de disparo
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {formatDate(selectedMessage.sent_date)}
                        </Text>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MensagensAtivas
